import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {
  ReportPromptField,
  ReportPromptOptions
} from '../../../store/reportStore/reportDialogStore';
import {
  computePopoverPosition,
  PopoverPosition,
  resolveAnchorRect
} from '../utils/reportDialogAnchors';

interface ReportInputModalProps {
  options: ReportPromptOptions;
  onClose: () => void;
}

function getPromptFields(options: ReportPromptOptions): ReportPromptField[] {
  if ('fields' in options && options.fields) {
    return options.fields;
  }

  return [
    {
      id: 'value',
      label: options.label ?? '',
      placeholder: options.placeholder,
      defaultValue: options.defaultValue,
      required: true
    }
  ];
}

function buildInitialValues(fields: ReportPromptField[]): Record<string, string> {
  return fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.id] = field.defaultValue ?? '';

    return acc;
  }, {});
}

const ReportInputModal: React.FC<ReportInputModalProps> = ({
  options,
  onClose
}) => {
  const {
    title,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    anchor,
    onCancel
  } = options;

  const isMulti = 'fields' in options && Boolean(options.fields);
  const fields = useMemo(() => getPromptFields(options), [options]);

  const [values, setValues] = useState(() => buildInitialValues(fields));
  const [position, setPosition] = useState<PopoverPosition>({
    top: 0,
    left: 0,
    placement: 'below'
  });
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const popoverRef = useRef<HTMLDivElement>(null);

  const fieldsKey = fields.map((f) => `${f.id}:${f.defaultValue ?? ''}`).join('|');

  useEffect(() => {
    setValues(buildInitialValues(fields));
    const timer = window.setTimeout(() => {
      const first = inputRefs.current[0];
      first?.focus();
      first?.select();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [fieldsKey]);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchorRect = resolveAnchorRect(anchor);
      const popoverHeight = popoverRef.current?.offsetHeight ?? 120;
      const popoverWidth = popoverRef.current?.offsetWidth ?? 300;
      setPosition(
        computePopoverPosition(anchorRect, popoverWidth, popoverHeight)
      );
    };

    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [anchor, title, fieldsKey]);

  const canSubmit = (): boolean => {
    const urlField = fields.find((f) => f.id === 'url');
    if (urlField) {
      return Boolean(values.url?.trim());
    }

    return fields.every((field) => {
      if (!field.required) {
        return true;
      }

      return Boolean(values[field.id]?.trim());
    });
  };

  const handleSubmit = () => {
    if (!canSubmit()) return;

    if (isMulti) {
      const trimmed = fields.reduce<Record<string, string>>((acc, field) => {
        acc[field.id] = values[field.id]?.trim() ?? '';

        return acc;
      }, {});
      (options.onSubmit as (values: Record<string, string>) => void)(trimmed);
    } else {
      const trimmed = values.value?.trim() ?? '';
      if (!trimmed) {
        return;
      }
      (options.onSubmit as (value: string) => void)(trimmed);
    }
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (index < fields.length - 1) {
        inputRefs.current[index + 1]?.focus();
        inputRefs.current[index + 1]?.select();

        return;
      }
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className="reportComponents">
      <div
        className="report-prompt-overlay"
        onMouseDown={handleCancel}
        role="presentation"
      >
        <div
          ref={popoverRef}
          className={`report-prompt-popover report-prompt-popover--${position.placement}`}
          style={{top: position.top, left: position.left}}
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-dialog-input-title"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="report-prompt-popover__arrow" aria-hidden="true" />
          <div className="report-prompt-popover__header">
            <h3 id="report-dialog-input-title">{title}</h3>
          </div>
          <div
            className={`report-prompt-popover__body${fields.length > 1 ? ' report-prompt-popover__body--stacked' : ''}`}
          >
            {fields.map((field, index) => (
              <div key={field.id} className="report-prompt-popover__field">
                {field.label && (
                  <label
                    className="report-dialog-label"
                    htmlFor={`report-dialog-input-${field.id}`}
                  >
                    {field.label}
                  </label>
                )}
                <input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  id={`report-dialog-input-${field.id}`}
                  type="text"
                  className="report-dialog-input"
                  value={values[field.id] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(e) => {
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.target.value
                    }));
                  }}
                  onKeyDown={(e) => {
                    handleKeyDown(e, index);
                  }}
                />
              </div>
            ))}
          </div>
          <div className="report-prompt-popover__footer">
            <button
              type="button"
              className="report-dialog-btn report-dialog-btn--cancel"
              onClick={handleCancel}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="report-dialog-btn report-dialog-btn--primary"
              onClick={handleSubmit}
              disabled={!canSubmit()}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportInputModal;
